import React from 'react';
import { Box, Typography, Badge } from '@mui/material';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { StateInformationPortalUrl, LicensingPortalUrl } from '../Constants';
import { AppRoutes } from '../helper/appRoutes';
import { AppFlags } from '../helper/config';
import { useInboxUnreadCount } from '../components/Icons/InboxNotificationBell';
import { useStateReportsOutstandingCount } from '../hooks/useStateReportsOutstandingCount';
import { CLIENT_HOME_ROLES, hasAnyRole, isSuperAdminRole } from '../helper/userRoles';

const ACCENT = '#2099d2';
const ACCENT_LIGHT = '#2099d214';
const BORDER = '#e2e8f0';

// Base (largest) tile metrics. Everything scales down from here as the number
// of visible tiles grows so the whole grid always fits on a single page.
const TILE_WIDTH_PX = 280;
const TILE_HEIGHT_PX = 200;
const TILE_GRID_GAP_PX = 48;
const ICON_CIRCLE_PX = 80;
const ICON_SVG_PX = 48;
const TILE_TITLE_PX = 20;

/**
 * Layout metrics derived from how many tiles are visible.
 * Columns are capped at 2 to keep the original portrait layout; when there are
 * more tiles we shrink every tile (via the scale factor) so the extra rows
 * still fit on a single page instead of spilling into a wide/landscape grid.
 */
const getTileLayout = (count: number) => {
  const columns = Math.min(Math.max(count, 1), 2);
  const rows = Math.ceil(count / columns);
  const scale = rows <= 2 ? 1 : rows === 3 ? 0.85 : rows === 4 ? 0.72 : 0.6;
  return {
    columns,
    scale,
    tileWidth: Math.round(TILE_WIDTH_PX * scale),
    tileHeight: Math.round(TILE_HEIGHT_PX * scale),
    gap: Math.max(24, Math.round(TILE_GRID_GAP_PX * scale)),
    iconCircle: Math.round(ICON_CIRCLE_PX * scale),
    iconSvg: Math.round(ICON_SVG_PX * scale),
    titleFont: Math.max(15, Math.round(TILE_TITLE_PX * scale)),
  };
};

const FolderPlusIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M56 16H32L26 8H8C5.79086 8 4 9.79086 4 12V52C4 54.2091 5.79086 56 8 56H56C58.2091 56 60 54.2091 60 52V20C60 17.7909 58.2091 16 56 16Z" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M32 28V44" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M24 36H40" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 8H16C13.7909 8 12 9.79086 12 12V52C12 54.2091 13.7909 56 16 56H48C50.2091 56 52 54.2091 52 52V20L40 8Z" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M40 8V20H52" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 28H44" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 36H44" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 44H32" stroke="#616161" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InboxIcon = () => (
  <svg fill="#26a69a" height="48" width="48" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 511.999 511.999">
    <g>
      <path d="M423.872,106.667H277.333V21.333C277.333,9.557,267.776,0,256,0s-21.333,9.557-21.333,21.333v85.333h-128
          C47.851,106.667,0,154.517,0,213.333V384c0,11.776,9.557,21.333,21.333,21.333H192v85.333c0,11.776,9.557,21.333,21.333,21.333
          h85.333c11.776,0,21.333-9.557,21.333-21.333v-85.333h170.667c11.797,0,21.333-9.557,21.333-21.333V194.795
          C512,146.219,472.448,106.667,423.872,106.667z M277.333,469.333h-42.667v-64h42.667V469.333z M469.334,362.667L469.334,362.667
          H298.667h-85.333V213.333c0-6.784-0.789-13.355-2.005-19.776c-0.384-2.048-1.003-4.011-1.515-6.016
          c-1.088-4.331-2.347-8.555-3.925-12.651c-0.853-2.176-1.771-4.267-2.752-6.357c-1.792-3.84-3.819-7.531-6.037-11.093
          c-1.152-1.856-2.24-3.733-3.499-5.504c-0.597-0.832-1.045-1.792-1.643-2.603h231.915c25.067,0,45.461,20.395,45.461,45.461
          V362.667z"/>
    </g>
  </svg>
);

const SettingsIcon = () => (
  <svg fill="#455a64" width="48" height="48" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg">
    <path d="m1739.34 1293.414-105.827 180.818-240.225-80.188-24.509 22.25c-69.91 63.586-150.211 109.666-238.644 136.771l-32.076 9.94-49.468 244.065H835.584l-49.468-244.179-32.076-9.939c-88.432-27.105-168.734-73.185-238.644-136.771l-24.508-22.25-240.226 80.189-105.826-180.82 189.74-164.442-7.453-32.978c-10.39-45.742-15.586-91.483-15.586-135.869 0-44.386 5.195-90.127 15.586-135.868l7.454-32.979-189.741-164.442 105.826-180.819 240.226 80.075 24.508-22.25c69.91-63.585 150.212-109.665 238.644-136.884l32.076-9.826 49.468-244.066h213.007l49.468 244.18 32.076 9.825c88.433 27.219 168.734 73.186 238.644 136.885l24.509 22.25 240.225-80.189 105.826 180.819-189.74 164.442 7.453 32.98c10.39 45.74 15.586 91.481 15.586 135.867 0 44.386-5.195 90.127-15.586 135.869l-7.454 32.978 189.741 164.556Zm-53.76-333.403c0-41.788-3.84-84.48-11.634-127.284l210.184-182.062-199.454-340.856-265.186 88.433c-66.974-55.567-143.322-99.388-223.85-128.414L1140.977.01H743.198l-54.663 269.704c-81.431 29.139-156.424 72.282-223.963 128.414L199.5 309.809.045 650.665l210.07 182.062c-7.68 42.804-11.52 85.496-11.52 127.284 0 41.789 3.84 84.48 11.52 127.172L.046 1269.357 199.5 1610.214l265.186-88.546c66.974 55.68 143.323 99.388 223.85 128.527l54.663 269.816h397.779l54.663-269.703c81.318-29.252 156.424-72.283 223.85-128.527l265.186 88.546 199.454-340.857-210.184-182.174c7.793-42.805 11.633-85.496 11.633-127.285ZM942.075 564.706C724.1 564.706 546.782 742.024 546.782 960c0 217.976 177.318 395.294 395.294 395.294 217.977 0 395.294-177.318 395.294-395.294 0-217.976-177.317-395.294-395.294-395.294m0 677.647c-155.633 0-282.353-126.72-282.353-282.353s126.72-282.353 282.353-282.353S1224.43 804.367 1224.43 960s-126.72 282.353-282.353 282.353" fill-rule="evenodd" />
  </svg>
);

const InfoIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#8b4513" strokeWidth="2.5" fill="none" />
    <path d="M32 44V32" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="32" cy="22" r="2" fill="#8b4513" />
  </svg>
);

const ArrowCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#8b4513" strokeWidth="2.5" fill="none" />
    <path d="M24 32H40" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M34 26L40 32L34 38" stroke="#8b4513" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalculatorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="10" width="36" height="44" rx="4" stroke="#1e88e5" strokeWidth="2.5" fill="none" />
    <path d="M22 20H42M22 28H28M34 28H42M22 36H28M34 36H42M22 44H42" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const DiligentFormIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M40 8H16C13.7909 8 12 9.79086 12 12V52C12 54.2091 13.7909 56 16 56H48C50.2091 56 52 54.2091 52 52V20L40 8Z"
      stroke="#1e88e5"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path d="M40 8V20H52" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 30H42" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M22 38H42" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M22 46H34" stroke="#1e88e5" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const ReportingIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 52V12" stroke="#7b1fa2" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M12 52H52" stroke="#7b1fa2" strokeWidth="2.5" strokeLinecap="round" />
    <rect x="18" y="32" width="8" height="16" rx="1" fill="#7b1fa2" />
    <rect x="30" y="24" width="8" height="24" rx="1" fill="#7b1fa2" />
    <rect x="42" y="16" width="8" height="32" rx="1" fill="#7b1fa2" />
  </svg>
);

interface DashboardTileProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  onClick: () => void;
  iconBgColor?: string;
  /** Optional unread/badge count shown on the icon (e.g. Inbox unread count) */
  badgeCount?: number;
  /** Computed sizing so tiles shrink as the tile count grows. */
  tileWidth?: number;
  tileHeight?: number;
  iconCircle?: number;
  iconSvg?: number;
  titleFont?: number;
}

const DashboardTile: React.FC<DashboardTileProps> = ({
  icon,
  title,
  onClick,
  iconBgColor = '#e3f2fd',
  badgeCount,
  tileWidth = TILE_WIDTH_PX,
  tileHeight = TILE_HEIGHT_PX,
  iconCircle = ICON_CIRCLE_PX,
  iconSvg = ICON_SVG_PX,
  titleFont = TILE_TITLE_PX,
}) => {
  const iconBox = (
    <Box
      sx={{
        width: `${iconCircle}px`,
        height: `${iconCircle}px`,
        minHeight: `${iconCircle}px`,
        borderRadius: '50%',
        backgroundColor: iconBgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: 1,
        // Scale every icon (inline SVGs and MUI icons) with the tile size.
        '& svg': {
          width: `${iconSvg}px`,
          height: `${iconSvg}px`,
          fontSize: `${iconSvg}px`,
        },
      }}
    >
      {icon}
    </Box>
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        width: '100%',
        maxWidth: tileWidth,
        height: tileHeight,
        padding: { xs: '20px 16px', md: '24px 20px' },
        border: `1px solid ${BORDER}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        '&:hover': {
          borderColor: ACCENT,
          backgroundColor: ACCENT_LIGHT,
          boxShadow: '0 4px 12px rgba(32, 153, 210, 0.12)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {badgeCount != null && badgeCount > 0 ? (
          <Badge badgeContent={badgeCount} color="error">
            {iconBox}
          </Badge>
        ) : (
          iconBox
        )}
      </Box>
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        mt: 0.5,
      }}>
        <Typography
          sx={{
            color: '#424242',
            fontSize: `${titleFont}px`,
            fontWeight: 700,
            textAlign: 'center',
            fontFamily: "'Proxima Nova', sans-serif",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

export const CustomerHomePage: React.FC = () => {
  const { user, isDiligentSearchUser, hasAnyDiligentPartner } = useAuth();
  const navigate = useNavigate();
  const inboxUnreadCount = useInboxUnreadCount();
  // Item 1: State Reports notification badge count, mirrors the Inbox badge
  const stateReportsOutstandingCount = useStateReportsOutstandingCount();

  const handleFilingQueueClick = () => {
    navigate(AppRoutes.client.base);
  };

  const handleStateReportsClick = () => {
    navigate(AppRoutes.client.stateReports);
  };

  const handleInboxClick = () => {
    navigate(AppRoutes.inbox);
  };

  const handleSettingsClick = () => {
    navigate(AppRoutes.client.notificationPreferences);
  };

  const handleStateInfoPortalClick = () => {
    window.open(StateInformationPortalUrl, '_blank');
  };

  const handleLicensingPortalClick = () => {
    window.open(LicensingPortalUrl, '_blank');
  };

  const handleDiligentSearchClick = () => {
    navigate(AppRoutes.home.diligentSearchLibrary);
  };

  const handleSurplusLinesTaxEstimatorClick = () => {
    navigate(AppRoutes.client.surplusLinesTaxEstimator);
  };

  const handleCreateDiligentSearchFormClick = () => {
    navigate(AppRoutes.client.createDiligentSearchForm);
  };

  const handleReportingClick = () => {
    navigate(AppRoutes.client.monthlyAccountPerformance);
  };

  const roles = user?.userRoles.split(",") ?? [];
  const hasRole = (...allowed: string[]) => allowed.some((role) => roles.includes(role));

  // Build only the tiles this user can see. Having them in an array lets us
  // size the grid from the count so everything fits on one page.
  const tiles: Array<Omit<DashboardTileProps, 'tileWidth' | 'tileHeight' | 'iconCircle' | 'iconSvg' | 'titleFont'> & { key: string }> = [];

  if (hasRole("Client Admin", "Client Processors")) {
    tiles.push({
      key: 'filing-queue',
      icon: <FolderPlusIcon />,
      title: 'Filing Queue',
      onClick: handleFilingQueueClick,
      iconBgColor: '#e3f2fd',
    });
  }
  if (AppFlags.enableStateReports && hasRole("Client Admin", "State Reporter")) {
    tiles.push({
      key: 'state-reports',
      icon: <DocumentIcon />,
      title: 'State Reports',
      onClick: handleStateReportsClick,
      iconBgColor: '#f5f5f5',
      badgeCount: stateReportsOutstandingCount,
    });
  }
  if (hasRole("Client Admin", "Client Processors", "State Reporter")) {
    tiles.push({
      key: 'inbox',
      icon: <InboxIcon />,
      title: 'Inbox',
      onClick: handleInboxClick,
      badgeCount: inboxUnreadCount,
    });
  }
  if (hasRole("Client Admin", "Client Processors", "State Reporter", "Diligent Search User", "Client Accounting", "Customer User")) {
    tiles.push({
      key: 'settings',
      icon: <SettingsIcon />,
      title: 'Settings',
      onClick: handleSettingsClick,
      iconBgColor: '#e3f2fd',
    });
  }
  if (isDiligentSearchUser === true && hasAnyDiligentPartner === true && hasRole("Client Admin", "Diligent Search User")) {
    tiles.push({
      key: 'diligent-search',
      icon: <LocalLibraryIcon sx={{ color: '#1e88e5' }} />,
      title: 'Diligent Search Library',
      onClick: handleDiligentSearchClick,
      iconBgColor: '#e3f2fd',
    });
  }
  if (user?.isSIPAccess) {
    tiles.push({
      key: 'state-info-portal',
      icon: <InfoIcon />,
      title: (
        <>
          Go to<br />
          State Information<br />
          Portal
        </>
      ),
      onClick: handleStateInfoPortalClick,
      iconBgColor: '#fff3e0',
    });
  }
  if (user?.isLicensingUser) {
    tiles.push({
      key: 'licensing-portal',
      icon: <ArrowCircleIcon />,
      title: (
        <>
          Go to<br />
          Licensing Portal
        </>
      ),
      onClick: handleLicensingPortalClick,
      iconBgColor: '#fff3e0',
    });
  }
  if (AppFlags.enableSurplusLinesTaxEstimator && isSuperAdminRole(user?.userRoles)) {
    tiles.push({
      key: 'surplus-lines-tax',
      icon: <CalculatorIcon />,
      title: 'Surplus Lines Tax Estimator',
      onClick: handleSurplusLinesTaxEstimatorClick,
      iconBgColor: '#e3f2fd',
    });
  }
  if (AppFlags.enableCreateDiligentSearchForm) {
    tiles.push({
      key: 'create-diligent-search-form',
      icon: <DiligentFormIcon />,
      title: 'Create Diligent Search Form',
      onClick: handleCreateDiligentSearchFormClick,
      iconBgColor: '#e3f2fd',
    });
  }
  if (hasAnyRole(user?.userRoles, CLIENT_HOME_ROLES)) {
    tiles.push({
      key: 'reporting',
      icon: <ReportingIcon />,
      title: 'Reporting',
      onClick: handleReportingClick,
      iconBgColor: '#f3e5f5',
    });
  }

  const layout = getTileLayout(tiles.length);

  return (
    <Box
      sx={{
        width: '100%',
        textAlign: 'center',
        px: { xs: 4, md: 0 },
        py: { xs: 4, md: 0 },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          mb: 4,
          mt: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: {
              xs: '100%',
              sm: `${layout.columns * layout.tileWidth + (layout.columns - 1) * layout.gap}px`,
            },
            mx: 'auto',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: `repeat(${layout.columns}, minmax(0, ${layout.tileWidth}px))`,
            },
            justifyContent: 'center',
            justifyItems: 'center',
            gap: `${layout.gap}px`,
          }}
        >
          {tiles.map(({ key, ...tile }) => (
            <DashboardTile
              key={key}
              {...tile}
              tileWidth={layout.tileWidth}
              tileHeight={layout.tileHeight}
              iconCircle={layout.iconCircle}
              iconSvg={layout.iconSvg}
              titleFont={layout.titleFont}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerHomePage;